<template>
  <div />
</template>

<script setup>
import onBlokkliEvent from "#blokkli/helpers/composables/onBlokkliEvent";
import { useBlokkli, onBeforeUnmount } from "#imports";
import {
  setBuffersAndAttributes,
  drawBufferInfo,
  setUniforms
} from "twgl.js";
import vs from "./vertex.glsl?raw";
import fs from "./fragment.glsl?raw";
import { RectangleBufferCollector } from "#blokkli/helpers/webgl";
import { toShaderColor } from "#blokkli/helpers";
const props = defineProps({
  blocks: { type: Array, required: true },
  gl: { type: null, required: true }
});
const { animation, theme, dom, ui } = useBlokkli();
const programInfo = animation.registerProgram("selection", props.gl, [vs, fs]);
class SelectionRectangleBufferCollector extends RectangleBufferCollector {
  uuids = [];
  lastCount = 0;
  prevKey = "";
  getBufferInfo(force) {
    const key = props.blocks.map((block) => {
      const uuid = block.uuid;
      const rect = dom.getBlockRect(uuid);
      if (!rect) {
        return uuid + "no_rect";
      }
      return uuid + rect.time;
    }).join("_");
    const hasChanged = force || this.prevKey !== key;
    if (hasChanged) {
      this.reset();
      this.lastCount = 0;
      for (let i = 0; i < props.blocks.length; i++) {
        const block = props.blocks[i];
        if (this.added.has(block.uuid)) {
          continue;
        }
        this.added.add(block.uuid);
        const el = dom.getDragElement(block);
        const rect = dom.getBlockRect(block.uuid);
        if (!rect || !el) {
          continue;
        }
        const style = theme.getDraggableStyle(el);
        this.addRectangle(
          {
            id: block.uuid,
            height: rect.height,
            width: rect.width,
            x: rect.x,
            y: rect.y,
            radius: style.radius,
            isInverted: style.isInverted
          },
          style.isInverted ? 1 : 0
        );
        this.lastCount++;
      }
      this.prevKey = key;
    }
    if (hasChanged) {
      this.bufferInfo = this.createBufferInfo();
    }
    return { info: this.bufferInfo, hasChanged };
  }
}
const collector = new SelectionRectangleBufferCollector(props.gl);
onBlokkliEvent("canvas:draw", (e) => {
  props.gl.useProgram(programInfo.program);
  setUniforms(programInfo, {
    u_color_default: toShaderColor(theme.accent.value[600]),
    u_color_inverted: [255, 255, 255],
    u_is_transforming: ui.isTransforming.value ? 1 : 0,
    u_time: e.time
  });
  animation.setSharedUniforms(props.gl, programInfo);
  const { info, hasChanged } = collector.getBufferInfo();
  if (!info) {
    return;
  }
  if (hasChanged) {
    setBuffersAndAttributes(props.gl, programInfo, info);
  }
  drawBufferInfo(props.gl, info, props.gl.TRIANGLES);
});
onBlokkliEvent("ui:resized", function() {
  collector.reset();
});
onBlokkliEvent("state:reloaded", function() {
  collector.reset();
});
onBeforeUnmount(() => {
  props.gl.clear(props.gl.COLOR_BUFFER_BIT);
});
</script>

<script>
export default {
  name: "SelectionOverlay"
};
</script>
